import type {
  UserDetailDTO,
  WorkspaceAccess,
  WorkspaceKey,
} from "@membentry/shared-types";

const OWNER_ROLE = "Owner";
const SUPERADMIN_ROLE = "SuperAdmin";
const WORKSPACE_HOME_PATHS: Record<WorkspaceKey, string> = {
  owner: "/dashboard",
  superadmin: "/superadmin",
};

type RoutedUser = { roles: readonly string[]; workspaceAccess?: WorkspaceAccess } | null | undefined;

export const CLUB_OPERATOR_ROLES = ["SuperAdmin", "Owner", "Manager", "Staff", "FrontDesk"] as const;

export const canOperateClubScanner = (roles: readonly string[] | null | undefined): boolean =>
  (roles ?? []).some((role) => (CLUB_OPERATOR_ROLES as readonly string[]).includes(role));

type AuthStoreCookieShape = {
  state?: {
    userData?: UserDetailDTO | null;
  };
};

export const deriveWorkspaceAccessFromRoles = (
  roles: readonly string[] | null | undefined,
): WorkspaceAccess => {
  const roleSet = new Set(roles ?? []);
  const available: WorkspaceKey[] = [];
  const canAccessOwnerWorkspace = roleSet.has(OWNER_ROLE);
  const canAccessSuperadminWorkspace = roleSet.has(SUPERADMIN_ROLE);

  if (canAccessOwnerWorkspace) {
    available.push("owner");
  }

  if (canAccessSuperadminWorkspace) {
    available.push("superadmin");
  }

  return {
    available,
    defaultWorkspace: available.length === 1 ? available[0] : null,
    canAccessOwnerWorkspace,
    canAccessSuperadminWorkspace,
  };
};

export const getUserWorkspaceAccess = (
  user: RoutedUser,
): WorkspaceAccess => {
  if (!user) {
    return deriveWorkspaceAccessFromRoles([]);
  }

  return user.workspaceAccess ?? deriveWorkspaceAccessFromRoles(user.roles);
};

export const getPreferredWorkspace = (
  workspaceAccess: WorkspaceAccess,
): WorkspaceKey | null => {
  return workspaceAccess.defaultWorkspace ?? workspaceAccess.available[0] ?? null;
};

export const getHomePath = (
  locale: string,
  user: RoutedUser,
): string => {
  const workspace = getPreferredWorkspace(getUserWorkspaceAccess(user));

  if (!workspace) {
    return canOperateClubScanner(user?.roles) ? `/${locale}/reception/scan` : `/${locale}/users`;
  }

  return `/${locale}${WORKSPACE_HOME_PATHS[workspace]}`;
};

export const canAccessPath = (
  pathWithoutLocale: string,
  user: RoutedUser,
): boolean => {
  const workspaceAccess = getUserWorkspaceAccess(user);
  if (pathWithoutLocale.startsWith("/dashboard")) {
    return workspaceAccess.available.length === 0
      ? true
      : workspaceAccess.canAccessOwnerWorkspace;
  }

  if (pathWithoutLocale.startsWith("/superadmin")) {
    return workspaceAccess.available.length === 0
      ? true
      : workspaceAccess.canAccessSuperadminWorkspace;
  }
  if (pathWithoutLocale.startsWith("/reception")) return canOperateClubScanner(user?.roles);
  return true;
};

export const parseAuthStoreCookie = (
  rawCookieValue: string | undefined,
): AuthStoreCookieShape | null => {
  if (!rawCookieValue) {
    return null;
  }

  try {
    return JSON.parse(decodeURIComponent(rawCookieValue)) as AuthStoreCookieShape;
  } catch {
    return null;
  }
};

export const getUserFromAuthStoreCookie = (rawCookieValue: string | undefined) =>
  parseAuthStoreCookie(rawCookieValue)?.state?.userData;
